/**
 * features/letters/LetterEditor.tsx
 * Compose a regulatory letter from a template, apply the director's
 * signature and the company stamp, then download and/or archive it.
 */
import { useMemo, useState } from 'react';
import { X, ScrollText, Download, Save, PenLine, Stamp, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { downloadLetterPdf } from '@/lib/letterPdf';
import { DEFAULT_COMPANY_SETTINGS, type CompanyDocument, type LetterKind } from '@/types';
import { LETTER_TEMPLATES, LETTER_LIST } from './letterTemplates';
import { saveLetter } from './lettersWrite';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function LetterEditor({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { settings, save } = useSettings();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;

  const [kind, setKind] = useState<LetterKind | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [date, setDate] = useState(todayISO());
  const [subject, setSubject] = useState('');
  const [recipient, setRecipient] = useState('');
  const [body, setBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);

  const [dirName, setDirName] = useState(company.directorName ?? '');
  const [dirTitle, setDirTitle] = useState(company.directorTitle ?? 'Director');
  const [signed, setSigned] = useState(false);
  const [stamped, setStamped] = useState(true);
  const [savingSig, setSavingSig] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savedRef, setSavedRef] = useState('');

  const template = kind ? LETTER_TEMPLATES[kind] : null;
  const hasSignature = !!company.directorSignature;
  const actor = user?.email ?? user?.uid ?? 'unknown';

  // Recompute subject/recipient/body from the template + values.
  function build(k: LetterKind, v: Record<string, string>, keepBody: boolean) {
    const t = LETTER_TEMPLATES[k];
    setSubject(t.subject(v));
    setRecipient(t.recipient(v));
    if (!keepBody) {
      setBody(t.body(v, company));
      setBodyEdited(false);
    }
  }
  function pick(k: LetterKind) {
    setKind(k);
    setValues({});
    build(k, {}, false);
  }
  function setField(key: string, val: string) {
    const v = { ...values, [key]: val };
    setValues(v);
    if (kind) build(kind, v, bodyEdited);
  }

  const transient: CompanyDocument = useMemo(
    () => ({
      id: '',
      kind: kind ?? 'general',
      reference: savedRef || 'DRAFT',
      subject,
      recipient,
      body,
      date,
      signed: signed && hasSignature,
      stamped,
      signatoryName: dirName,
      signatoryTitle: dirTitle,
      createdAt: '',
      createdBy: '',
    }),
    [kind, savedRef, subject, recipient, body, date, signed, hasSignature, stamped, dirName, dirTitle],
  );

  async function onSignatureFile(file: File) {
    if (!settings) {
      setError('Save company settings before adding a signature.');
      return;
    }
    setSavingSig(true);
    setError('');
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.onerror = () => rej(new Error('Could not read the image.'));
        r.readAsDataURL(file);
      });
      await save({ ...settings, directorSignature: dataUrl, directorName: dirName, directorTitle: dirTitle });
      setSigned(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the signature.');
    } finally {
      setSavingSig(false);
    }
  }

  async function persistSignatory() {
    if (settings && (settings.directorName !== dirName || settings.directorTitle !== dirTitle)) {
      await save({ ...settings, directorName: dirName, directorTitle: dirTitle });
    }
  }

  function download() {
    void persistSignatory();
    downloadLetterPdf(transient, company);
  }

  async function archive() {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await persistSignatory();
      const res = await saveLetter(
        {
          kind: kind ?? 'general',
          subject: subject.trim(),
          recipient: recipient.trim(),
          body: body.trim(),
          date,
          signed: signed && hasSignature,
          stamped,
          signatoryName: dirName.trim() || undefined,
          signatoryTitle: dirTitle.trim() || undefined,
        },
        actor,
      );
      setSavedRef(res.reference);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not archive the letter.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-8 w-full max-w-3xl">
        <div className={glassSurface('p-5 space-y-4')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <ScrollText size={18} />
              <h2 className="text-base font-semibold text-white">
                {template ? template.label : 'New company letter'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Type picker */}
          {!kind ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {LETTER_LIST.map((t) => (
                <button
                  key={t.kind}
                  onClick={() => pick(t.kind)}
                  className="rounded-xl border border-black/10 p-3 text-left transition hover:border-brand hover:bg-brandSoft/50 dark:border-white/10 dark:hover:bg-white/5"
                >
                  <div className="font-semibold text-primary dark:text-gray-100">{t.label}</div>
                  <div className="mt-0.5 text-xs text-primaryLight dark:text-gray-400">{t.blurb}</div>
                </button>
              ))}
            </div>
          ) : savedRef ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-300">
                Archived as <span className="font-semibold">{savedRef}</span>.
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" icon={<Download size={16} />} onClick={download}>
                  Download PDF
                </Button>
                <Button onClick={onClose}>Done</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Template fields */}
              <div className="grid gap-3 sm:grid-cols-2">
                {template!.fields.map((f) =>
                  f.type === 'textarea' ? (
                    <div key={f.key} className="sm:col-span-2">
                      <Field label={f.label}>
                        <Textarea
                          rows={3}
                          placeholder={f.placeholder}
                          value={values[f.key] ?? ''}
                          onChange={(e) => setField(f.key, e.target.value)}
                        />
                      </Field>
                    </div>
                  ) : (
                    <Field key={f.key} label={f.label}>
                      <Input
                        type={f.type === 'date' ? 'date' : 'text'}
                        placeholder={f.placeholder}
                        value={values[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    </Field>
                  ),
                )}
                <Field label="Letter date">
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
              </div>

              {/* Generated, editable letter */}
              <Field label="Recipient">
                <Textarea rows={3} value={recipient} onChange={(e) => setRecipient(e.target.value)} />
              </Field>
              <Field label="Subject">
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </Field>
              <Field label="Body" hint="Prefilled from the template — edit freely.">
                <Textarea
                  rows={9}
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setBodyEdited(true);
                  }}
                />
              </Field>

              {/* Signatory */}
              <div className="rounded-xl border border-black/10 p-3 dark:border-white/10">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                  Signatory
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Director name">
                    <Input value={dirName} onChange={(e) => setDirName(e.target.value)} />
                  </Field>
                  <Field label="Title">
                    <Input value={dirTitle} onChange={(e) => setDirTitle(e.target.value)} />
                  </Field>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={signed && hasSignature}
                      disabled={!hasSignature}
                      onChange={(e) => setSigned(e.target.checked)}
                    />
                    <PenLine size={15} /> Apply signature {hasSignature ? '(on file)' : '(none uploaded)'}
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-brand">
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => e.target.files?.[0] && onSignatureFile(e.target.files[0])} />
                    <span className="rounded-lg border border-brand/40 px-2 py-1 text-xs font-semibold hover:bg-brandSoft">
                      {savingSig ? 'Uploading…' : hasSignature ? 'Replace signature' : 'Upload signature'}
                    </span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" checked={stamped} onChange={(e) => setStamped(e.target.checked)} />
                    <Stamp size={15} /> Apply company stamp
                  </label>
                </div>
                <p className="mt-2 text-xs text-primaryLight dark:text-gray-400">
                  Tip: upload a transparent-background PNG of the signature for the cleanest result.
                </p>
              </div>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button variant="ghost" onClick={() => setKind(null)}>
                  ← Change type
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" icon={<Download size={16} />} onClick={download}>
                    Download PDF
                  </Button>
                  <Button icon={savedRef ? <Check size={16} /> : <Save size={16} />} onClick={archive} loading={busy}>
                    Save &amp; archive
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
